import React from 'react';
import Reveal from './Reveal';
import QssnLogo from './QssnLogo';

const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };
const ACCENT = 'var(--accent)';
const SUCCESS = '#147A55';

// Per-network series colours — shared across the charts and the network lanes.
const NET_BLUE = '#2B57C9';
const NET_TEAL = '#0E8E7B';
const NET_VIOLET = '#8A5CB8';

// The dashboard reads best a touch narrower than the copy above it.
const FRAME_MAX = 960;

const microLabel: React.CSSProperties = {
  ...mono,
  fontSize: '.64rem',
  letterSpacing: '.13em',
  textTransform: 'uppercase',
  color: 'var(--ink-3)',
};

type Tone = 'accent' | 'success' | 'neutral';

type Metric = {
  label: string;
  value: string;
  detail: string;
  tone?: Tone;
};

type Network = {
  name: string;
  color: string;
  uptime: string;
  bundle: string;
  balance: string;
  balancePercent: number;
  recent: number[];
};

type Serie = { name: string; color: string; values: number[] };

const metrics: Metric[] = [
  { label: 'Assets under governance', value: '$3.2B', detail: 'across 14 programs' },
  { label: 'Exposure prevented', value: '$46.2M', detail: '8 unauthorized, past 90 days' },
  { label: 'Privileged actions', value: '1,284', detail: 'authorized, past 24h' },
  { label: 'Largest authorization', value: '$5.0M', detail: 'treasury mint, past 90 days' },
];

const networks: Network[] = [
  { name: 'Kaia', color: NET_BLUE, uptime: '1.66 weeks', bundle: '4.00s', balance: '50.1383 ETH', balancePercent: 100, recent: [3.8, 4.1, 4.0, 4.2, 3.9, 4.0, 4.1, 3.9, 4.0, 4.0] },
  { name: 'Base', color: NET_TEAL, uptime: '1.54 weeks', bundle: '5.10s', balance: '9.0594 ETH', balancePercent: 18, recent: [5.2, 5.0, 5.1, 5.3, 5.0, 5.1, 4.9, 5.1, 5.2, 5.1] },
  { name: 'Ethereum', color: NET_VIOLET, uptime: '4.80 days', bundle: '7.13s', balance: '3.3600 ETH', balancePercent: 7, recent: [6.9, 7.2, 7.1, 7.3, 7.0, 7.4, 7.1, 6.9, 7.2, 7.13] },
];

// Cumulative value authorized across the window (illustrative shape).
const CUMULATIVE = [8, 10, 13, 14, 16, 19, 22, 23, 26, 29, 31, 34, 36, 39, 43, 45, 48, 52, 54, 57, 61, 64, 67, 71, 74, 78, 82, 86, 90, 95];

const valueByAction: Array<{ label: string; value: string; pct: number }> = [
  { label: 'Treasury mint', value: '$3.20B', pct: 100 },
  { label: 'Reserve transfer', value: '$1.92B', pct: 60 },
  { label: 'Redemption burn', value: '$0.90B', pct: 28 },
  { label: 'Policy & configuration', value: '$0.40B', pct: 13 },
];

const throughput: Serie[] = [
  { name: 'kaia', color: NET_BLUE, values: [22, 25, 20, 28, 24, 30, 26, 23, 27, 25, 29, 24, 26, 28, 23, 25, 27, 24, 29, 26, 24, 28, 25, 27, 23, 26, 28, 25, 24, 27] },
  { name: 'base', color: NET_TEAL, values: [14, 16, 13, 18, 15, 17, 14, 16, 19, 15, 13, 17, 16, 18, 14, 16, 15, 17, 14, 16, 18, 15, 14, 16, 17, 15, 16, 14, 17, 16] },
  { name: 'ethereum', color: NET_VIOLET, values: [7, 9, 6, 10, 8, 7, 9, 8, 6, 9, 7, 8, 10, 7, 6, 8, 9, 7, 8, 6, 9, 8, 7, 9, 6, 8, 7, 9, 8, 7] },
];

const latency: Array<{ name: string; color: string; value: string; pct: number }> = [
  { name: 'kaia', color: NET_BLUE, value: '1.2s', pct: 24 },
  { name: 'base', color: NET_TEAL, value: '2.7s', pct: 53 },
  { name: 'ethereum', color: NET_VIOLET, value: '5.1s', pct: 100 },
];

const colorForTone = (tone: Tone = 'accent') => {
  if (tone === 'neutral') return 'color-mix(in srgb, var(--ink) 48%, var(--bg-alt))';
  if (tone === 'success') return 'color-mix(in srgb, var(--accent) 82%, var(--ink))';
  return 'color-mix(in srgb, var(--accent) 82%, var(--ink))';
};

const Dot: React.FC<{ tone?: Tone }> = ({ tone = 'accent' }) => (
  <span style={{ width: 7, height: 7, borderRadius: '50%', background: tone === 'success' ? SUCCESS : colorForTone(tone), flex: '0 0 auto' }} />
);

const StatusPill: React.FC<{ children: React.ReactNode; tone?: Tone }> = ({ children, tone = 'accent' }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '.4rem',
      padding: '.38rem .62rem',
      borderRadius: 999,
      border: '1px solid rgba(21,23,28,.12)',
      background: 'rgba(255,255,255,.72)',
      color: tone === 'success' ? SUCCESS : ACCENT,
      ...mono,
      fontSize: '.62rem',
      fontWeight: 500,
      letterSpacing: '.08em',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    }}
  >
    <Dot tone={tone} />
    {children}
  </span>
);

const SectionDivider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '.85rem', margin: '.15rem 0 .7rem' }}>
    <span style={microLabel}>{children}</span>
    <span style={{ flex: 1, height: 1, background: 'rgba(21,23,28,.08)' }} />
  </div>
);

const MetricCard: React.FC<Metric> = ({ label, value, detail }) => (
  <div
    style={{
      padding: '.9rem .95rem',
      border: '1px solid rgba(21,23,28,.1)',
      borderRadius: 12,
      background: 'rgba(255,255,255,.66)',
      minWidth: 0,
    }}
  >
    <div style={microLabel}>{label}</div>
    <div style={{ marginTop: '.45rem', fontSize: '1.5rem', lineHeight: 1, fontWeight: 500, letterSpacing: '-.02em', color: 'var(--ink)' }}>{value}</div>
    <div style={{ marginTop: '.55rem', ...mono, fontSize: '.68rem', lineHeight: 1.35, color: 'var(--ink-3)' }}>{detail}</div>
  </div>
);

const BarTrack: React.FC<{ percent: number; tone?: Tone }> = ({ percent, tone = 'accent' }) => (
  <div style={{ height: 7, borderRadius: 999, background: 'rgba(21,23,28,.08)', overflow: 'hidden' }}>
    <div
      style={{
        width: `${Math.max(0, Math.min(100, percent))}%`,
        height: '100%',
        borderRadius: 999,
        background: colorForTone(tone),
      }}
    />
  </div>
);

type Pt = [number, number];

const toPoints = (vals: number[], max: number, W: number, H: number, pT: number, pB: number): Pt[] => {
  const n = vals.length;
  return vals.map((v, i) => [n <= 1 ? 0 : (W * i) / (n - 1), pT + (H - pT - pB) - (max <= 0 ? 0 : v / max) * (H - pT - pB)]);
};

// Rounded line: Catmull-Rom spline rendered as cubic béziers, so the chart curves rather than zig-zags.
const smoothLine = (pts: Pt[]): string => {
  if (pts.length < 2) return pts.length ? `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}` : '';
  const t = 0.16;
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) * t;
    const c1y = p1[1] + (p2[1] - p0[1]) * t;
    const c2x = p2[0] - (p3[0] - p1[0]) * t;
    const c2y = p2[1] - (p3[1] - p1[1]) * t;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
};

// Cumulative value-secured area chart — the up-and-to-the-right business hero.
const ValueChart: React.FC<{ values: number[]; height?: number }> = ({ values, height = 140 }) => {
  const W = 800;
  const H = height;
  const pT = 10;
  const pB = 6;
  // Floor-anchored scale: the baseline is the window's low, not zero. The panel's y-axis
  // labels state the real floor, so the curve reads as "already high, still climbing" — not
  // as $4.28B accumulating from $0.
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const plotH = H - pT - pB;
  const pts: Pt[] = values.map((v, i) => [values.length <= 1 ? 0 : (W * i) / (values.length - 1), pT + plotH - ((v - min) / range) * plotH]);
  const line = smoothLine(pts);
  const area = `${line} L ${W} ${H - pB} L 0 ${H - pB} Z`;
  return (
    <svg className="qssn-preview-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      <defs>
        <linearGradient id="qssn-value-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.2} />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity={0} />
        </linearGradient>
      </defs>
      {[0.25, 0.5, 0.75, 1].map((g) => (
        <line key={g} x1={0} x2={W} y1={pT + (H - pT - pB) * (1 - g)} y2={pT + (H - pT - pB) * (1 - g)} style={{ stroke: 'rgba(21,23,28,.07)' }} vectorEffect="non-scaling-stroke" />
      ))}
      <path d={area} style={{ fill: 'url(#qssn-value-fill)', stroke: 'none' }} />
      <path d={line} style={{ fill: 'none', stroke: 'var(--accent)', strokeWidth: 2, strokeLinejoin: 'round', strokeLinecap: 'round' }} vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const TimeSeries: React.FC<{ series: Serie[]; height?: number }> = ({ series, height = 140 }) => {
  const W = 800;
  const H = height;
  const pT = 8;
  const pB = 6;
  const max = Math.max(1, ...series.flatMap((s) => s.values)) * 1.15;
  return (
    <svg className="qssn-preview-chart" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      {[0.25, 0.5, 0.75, 1].map((g) => (
        <line key={g} x1={0} x2={W} y1={pT + (H - pT - pB) * (1 - g)} y2={pT + (H - pT - pB) * (1 - g)} style={{ stroke: 'rgba(21,23,28,.07)' }} vectorEffect="non-scaling-stroke" />
      ))}
      {series.map((s) => (
        <path
          key={s.name}
          d={smoothLine(toPoints(s.values, max, W, H, pT, pB))}
          style={{ fill: 'none', stroke: s.color, strokeWidth: 1.8, strokeLinejoin: 'round', strokeLinecap: 'round' }}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
};

const NetworkLegend: React.FC<{ items: Array<{ name: string; color: string; value: string }> }> = ({ items }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem 1.2rem', marginTop: '.7rem' }}>
    {items.map((it) => (
      <span key={it.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '.42rem', ...mono, fontSize: '.66rem', color: 'var(--ink-3)' }}>
        <span style={{ width: 14, height: 2, background: it.color, borderRadius: 2 }} />
        {it.name}
        <span style={{ color: 'var(--ink)' }}>{it.value}</span>
      </span>
    ))}
  </div>
);

const Axis: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'space-between', ...mono, fontSize: '.6rem', color: 'var(--ink-3)', marginTop: '.4rem' }}>
    <span>-24h</span>
    <span>-12h</span>
    <span>now</span>
  </div>
);

const Sparkline: React.FC<{ values: number[]; color: string; height?: number }> = ({ values, color, height = 26 }) => {
  const W = 120;
  const H = height;
  const pT = 3;
  const pB = 3;
  const max = Math.max(...values) * 1.08;
  const min = Math.min(...values) * 0.96;
  const range = max - min || 1;
  const pts: Pt[] = values.map((v, i) => [values.length <= 1 ? 0 : (W * i) / (values.length - 1), pT + (H - pT - pB) - ((v - min) / range) * (H - pT - pB)]);
  const d = smoothLine(pts);
  const area = `${d} L ${W} ${H - pB} L 0 ${H - pB} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H, display: 'block' }}>
      <path d={area} style={{ fill: color, fillOpacity: 0.09, stroke: 'none' }} />
      <path d={d} style={{ fill: 'none', stroke: color, strokeWidth: 1.5, strokeLinejoin: 'round', strokeLinecap: 'round' }} vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

const Panel: React.FC<{ label: string; title: string; status?: React.ReactNode; children: React.ReactNode }> = ({ label, title, status, children }) => (
  <section
    className="qssn-preview-panel"
    style={{
      padding: '1rem 1.05rem',
      border: '1px solid rgba(21,23,28,.1)',
      borderRadius: 14,
      background: 'rgba(255,255,255,.64)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
      <div>
        <div style={microLabel}>{label}</div>
        <div style={{ marginTop: '.36rem', fontSize: '1rem', lineHeight: 1.25, fontWeight: 500, color: 'var(--ink)' }}>{title}</div>
      </div>
      {status}
    </div>
    {children}
  </section>
);

const ValuePanel: React.FC = () => (
  <section
    className="qssn-preview-panel"
    style={{
      padding: '1rem 1.05rem',
      border: '1px solid rgba(21,23,28,.1)',
      borderRadius: 14,
      background: 'rgba(255,255,255,.64)',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
      <div>
        <div style={microLabel}>Transaction value authorized</div>
        <div style={{ marginTop: '.5rem', display: 'flex', alignItems: 'baseline', gap: '.6rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '1.65rem', lineHeight: 1, fontWeight: 500, letterSpacing: '-.02em', color: 'var(--ink)' }}>$6.42B</span>
          <span style={{ ...mono, fontSize: '.7rem', color: 'var(--ink-2)' }}>▲ +$180M past 24h</span>
        </div>
        <div style={{ marginTop: '.4rem', ...mono, fontSize: '.66rem', color: 'var(--ink-3)' }}>running total to date, post-quantum signed</div>
      </div>
    </div>
    <div style={{ marginTop: '.9rem', display: 'flex', gap: '.55rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: 140, paddingTop: 2, paddingBottom: 2, ...mono, fontSize: '.58rem', color: 'var(--ink-3)' }}>
        <span>$6.42B</span>
        <span>$6.24B</span>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ValueChart values={CUMULATIVE} />
        <Axis />
      </div>
    </div>
  </section>
);

const NetworkRow: React.FC<Network> = ({ name, color, uptime, bundle, balance, balancePercent, recent }) => (
  <div
    className="qssn-preview-network-row"
    style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(7rem,1fr) minmax(6rem,.8fr) minmax(8rem,1fr) minmax(9rem,1.15fr)',
      gap: '1rem',
      alignItems: 'center',
      padding: '.82rem 0',
      borderTop: '1px solid rgba(21,23,28,.1)',
    }}
  >
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flex: '0 0 auto' }} />
        <div style={{ fontSize: '.95rem', fontWeight: 500, color: 'var(--ink)' }}>{name}</div>
      </div>
      <div style={{ marginTop: '.28rem', ...mono, fontSize: '.66rem', letterSpacing: '.08em', color: SUCCESS }}>UP</div>
    </div>
    <div>
      <span className="qssn-preview-cell-label">Uptime</span>
      <div style={{ ...mono, fontSize: '.78rem', color: 'var(--ink)' }}>{uptime}</div>
    </div>
    <div>
      <span className="qssn-preview-cell-label">Bundle</span>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.65rem', marginBottom: '.35rem' }}>
        <span style={{ ...mono, fontSize: '.78rem', color: 'var(--ink)' }}>{bundle}</span>
        <span style={{ ...mono, fontSize: '.62rem', color: 'var(--ink-3)' }}>10m</span>
      </div>
      <Sparkline values={recent} color={color} />
    </div>
    <div>
      <span className="qssn-preview-cell-label">Operator funds</span>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '.65rem', marginBottom: '.42rem' }}>
        <span style={{ ...mono, fontSize: '.78rem', color: 'var(--ink)' }}>{balance}</span>
      </div>
      <BarTrack percent={balancePercent} tone="neutral" />
    </div>
  </div>
);

const toolChip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '.32rem',
  padding: '.34rem .55rem',
  borderRadius: 7,
  border: '1px solid rgba(21,23,28,.14)',
  background: 'rgba(255,255,255,.7)',
  ...mono,
  fontSize: '.66rem',
  color: 'var(--ink-2)',
  whiteSpace: 'nowrap',
};

const toolBtn: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 26,
  height: 26,
  borderRadius: 7,
  border: '1px solid rgba(21,23,28,.14)',
  background: 'rgba(255,255,255,.7)',
};

// Grafana-style control cluster: datasource · time range · refresh · interval.
const HeaderTools: React.FC = () => (
  <div className="qssn-preview-tabs" style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
    <span style={toolChip}>Prometheus</span>
    <span style={toolChip}>
      Last 24h
      <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ stroke: 'var(--ink-3)', strokeWidth: 1.3, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
        <path d="M1.6 3 4 5.4 6.4 3" />
      </svg>
    </span>
    <span style={toolBtn} aria-hidden>
      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ stroke: 'var(--ink-2)', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' }}>
        <path d="M13.2 6.6A5.3 5.3 0 1 0 13.5 9" />
        <path d="M13.5 3.3v3.3h-3.3" />
      </svg>
    </span>
    <span style={{ ...toolBtn, borderRadius: '50%' }} aria-hidden>
      <span style={{ width: 12, height: 12, borderRadius: '50%', border: '1.4px solid var(--ink-3)' }} />
    </span>
  </div>
);

// Render the visitor's current local date + 24h time, e.g. "Jun 30 · 14:07".
const localTimestamp = () => {
  const d = new Date();
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${date}, ${time}`;
};

const ControlPlanePreview: React.FC = () => (
  <div
    className="qssn-preview-frame"
    style={{
      maxWidth: FRAME_MAX,
      margin: '0 auto',
      overflow: 'hidden',
      background: 'linear-gradient(180deg, rgba(255,255,255,.96), rgba(250,250,247,.9))',
      border: '1px solid rgba(21,23,28,.14)',
      borderRadius: 24,
      boxShadow: '0 1px 2px rgba(16,18,24,.05), 0 40px 100px -64px rgba(16,18,24,.42)',
    }}
  >
    <div
      className="qssn-preview-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '1rem 1.15rem',
        borderBottom: '1px solid rgba(21,23,28,.1)',
        background: 'rgba(255,255,255,.8)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem' }}>
        <QssnLogo height={18} style={{ color: 'var(--ink)' }} />
        <span style={{ width: 1, height: 22, background: 'var(--line)' }} />
        <div>
          <div style={{ fontSize: '.95rem', fontWeight: 500, color: 'var(--ink)' }}>Control Plane</div>
        </div>
      </div>
      <HeaderTools />
    </div>

    <main className="qssn-preview-main" style={{ padding: '1.25rem 1.4rem 1.35rem' }}>
      <div className="qssn-preview-title-row" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <div style={microLabel}>Issuer controls</div>
          <h3 style={{ margin: '.42rem 0 0', fontSize: '1.5rem', lineHeight: 1.1, fontWeight: 500, letterSpacing: '-.02em', color: 'var(--ink)' }}>
            Authorization overview
          </h3>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ ...mono, fontSize: '.7rem', color: 'var(--ink-3)' }}>Last updated</div>
          <div style={{ marginTop: '.32rem', ...mono, fontSize: '.78rem', color: 'var(--ink)' }}>{localTimestamp()}</div>
        </div>
      </div>

      <div className="qssn-preview-metrics qssn-preview-metrics-five" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: '.65rem', marginBottom: '.8rem' }}>
        {metrics.map((metric) => (
          <MetricCard key={metric.label} {...metric} />
        ))}
      </div>

      <div className="qssn-preview-unified-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.8fr) minmax(0,1fr)', gap: '.8rem', marginBottom: '1rem' }}>
        <ValuePanel />
        <Panel label="Authorized value" title="By action type">
          <div style={{ display: 'grid', gap: '.85rem', marginTop: '.1rem' }}>
            {valueByAction.map((a) => (
              <div key={a.label}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.5rem', marginBottom: '.42rem' }}>
                  <span style={{ fontSize: '.8rem', color: 'var(--ink-2)' }}>{a.label}</span>
                  <span style={{ ...mono, fontSize: '.75rem', color: 'var(--ink)' }}>{a.value}</span>
                </div>
                <BarTrack percent={a.pct} />
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <SectionDivider>Bundler telemetry</SectionDivider>

      <div className="qssn-preview-unified-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.8fr) minmax(0,1fr)', gap: '.8rem', marginBottom: '.8rem' }}>
        <Panel label="Mempool &amp; UserOps" title="UserOp throughput" status={<StatusPill>24h</StatusPill>}>
          <div style={{ ...mono, fontSize: '.62rem', color: 'var(--ink-3)', marginBottom: '.35rem' }}>attempts per minute</div>
          <TimeSeries series={throughput} />
          <Axis />
          <NetworkLegend items={throughput.map((s) => ({ name: s.name, color: s.color, value: `${s.values[s.values.length - 1]}` }))} />
        </Panel>
        <Panel label="Mempool → submission" title="Submission latency">
          <div style={{ ...mono, fontSize: '.62rem', color: 'var(--ink-3)', marginBottom: '.35rem' }}>p95 latency</div>
          <div className="qssn-preview-latency-chart" style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', height: 150, borderBottom: '1px solid rgba(21,23,28,.12)' }}>
            {latency.map((l) => (
              <div key={l.name} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                <span style={{ ...mono, fontSize: '.72rem', color: 'var(--ink)', marginBottom: '.42rem' }}>{l.value}</span>
                <div style={{ width: '100%', maxWidth: 46, height: `${Math.max(6, l.pct * 0.82)}%`, background: l.color, borderRadius: '5px 5px 0 0' }} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '.5rem' }}>
            {latency.map((l) => (
              <span key={l.name} style={{ flex: 1, textAlign: 'center', ...mono, fontSize: '.64rem', color: 'var(--ink-3)' }}>{l.name}</span>
            ))}
          </div>
        </Panel>
      </div>

      <Panel label="Bundler instances" title="Uptime, last bundle, and balances">
        <div
          className="qssn-preview-table-head"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(7rem,1fr) minmax(6rem,.8fr) minmax(8rem,1fr) minmax(9rem,1.15fr)',
            gap: '1rem',
            paddingBottom: '.45rem',
            ...mono,
            fontSize: '.62rem',
            letterSpacing: '.11em',
            textTransform: 'uppercase',
            color: 'var(--ink-3)',
          }}
        >
          <span>Network</span>
          <span>Uptime</span>
          <span>Last bundle</span>
          <span>OperatorManager balance</span>
        </div>
        {networks.map((network) => (
          <NetworkRow key={network.name} {...network} />
        ))}
      </Panel>
    </main>
  </div>
);

const monoBrand: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };

const LiveMonitoring: React.FC = () => (
  <section className="qssn-monitoring-section" style={{ background: 'var(--bg-alt)', padding: '7rem 1.5rem 7.5rem' }}>
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Reveal
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          borderTop: '1px solid var(--line)',
          paddingTop: '1.25rem',
          marginBottom: '3.5rem',
        }}
      >
        <span style={{ ...monoBrand, fontSize: '.6875rem', letterSpacing: '.18em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>02</span>
      </Reveal>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4rem', marginBottom: '3rem' }}>
        <Reveal
          as="h2"
          style={{
            flex: '1 1 400px',
            margin: 0,
            fontWeight: 500,
            fontSize: 'clamp(2rem,3.4vw,3.1rem)',
            lineHeight: 1.05,
            letterSpacing: '-.02em',
            color: 'var(--ink)',
            textWrap: 'balance',
          } as React.CSSProperties}
        >
          The control plane for high-value authorization.
        </Reveal>
        <Reveal as="p" style={{ flex: '1 1 420px', margin: 0, fontSize: '1.125rem', lineHeight: 1.6, color: 'var(--ink-2)', alignSelf: 'center' }}>
          QSSN gives regulated issuers one operator-ready view — the value under quantum-safe authorization on top, and the live bundler telemetry that proves it beneath.
        </Reveal>
      </div>

      <Reveal delay={80}>
        <ControlPlanePreview />
      </Reveal>
    </div>
  </section>
);

export default LiveMonitoring;
